import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchNotifications, markNotificationsRead } from "../utils/apiService";
import { useOrg } from "../utils/orgContext";

const REFRESH_MS = 60000;

const formatWhen = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || isNaN(date)) return "";
  return date.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

/** The NavBar bell: the user's notifications in the selected org, with an unread count. */
const NotificationBell = ({ getAccessTokenSilently }) => {
  const { orgId, ready } = useOrg();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [failed, setFailed] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    if (!ready) return;
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const data = await fetchNotifications(token, orgId);
      setItems(data?.values || []);
      setUnread(data?.unread || 0);
      setFailed(false);
    } catch (e) {
      setFailed(true);
    }
  }, [getAccessTokenSilently, orgId, ready]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = async () => {
    const opening = !open;
    setOpen(opening);
    if (opening && unread > 0) {
      try {
        let token = null;
        try { token = await getAccessTokenSilently(); } catch (e) {}
        await markNotificationsRead(items.filter((n) => !n.read).map((n) => n.id), token, orgId);
        setUnread(0);
      } catch (e) {
        // Leave them unread; the next refresh shows the true state
      }
    }
  };

  return (
    <div ref={panelRef} style={{ position: "relative" }}>
      <button
        title="Notifications"
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
        onClick={toggle}
        style={{
          background: "none", border: "none", padding: 0, cursor: "pointer",
          position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
          width: "44px", height: "44px", borderRadius: "50%",
          color: "#4a5568"
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{
            position: "absolute", top: "6px", right: "4px",
            minWidth: "16px", height: "16px", padding: "0 4px", borderRadius: "8px",
            backgroundColor: "var(--color-danger)", color: "white", border: "2px solid white",
            fontSize: "0.6rem", fontWeight: 700, lineHeight: "12px", textAlign: "center"
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "48px", width: "320px", maxHeight: "380px", overflowY: "auto",
          background: "white", border: "1px solid #e9ecef", borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 1050
        }}>
          <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid #f0f0f0", fontSize: "0.82rem", fontWeight: 700, color: "#1a1d23" }}>
            Notifications
          </div>
          {failed ? (
            <div style={{ padding: "1rem", fontSize: "0.82rem", color: "#6c757d" }}>Notifications could not be loaded.</div>
          ) : items.length === 0 ? (
            <div style={{ padding: "1rem", fontSize: "0.82rem", color: "#6c757d" }}>Nothing yet.</div>
          ) : (
            items.map((n) => (
              <div key={n.id} style={{ padding: "0.65rem 1rem", borderBottom: "1px solid #f7f8fa", background: n.read ? "white" : "#f5f8ff" }}>
                <div style={{ fontSize: "0.8rem", color: "#343a40" }}>{n.message}</div>
                <div style={{ fontSize: "0.68rem", color: "#adb5bd", marginTop: "0.2rem" }}>{formatWhen(n.createdAt)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
